// * External
import _ from "lodash";
import { ChevronRightOutlined } from "@toddle-edu/ds-icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, EmptyState, SideNavigation } from "@toddle-edu/ds-web";
import { NoServiceAsActionInsightsIllustration } from "@toddle-edu/ds-theme";
import classNames from "classnames";

// * Internal
import { useI18n } from "Hooks";
import { ELEMENT_TYPE_MYP_OBJECTIVES } from "Constants/stringConstants";

// * Relative
import {
  getAllLeafNodes,
  getLearningGoalsWithFormattedOptionsForSideNav,
} from "../../../../module/AssessmentLearningGoalsUtils";
import classes from "./AssessmentOtherLearningGoalsModalBody.scss";

// * Common Navigation Component
const CommonSideNavigation = ({
  type,
  options,
  onClick,
  onToggle,
  isChecked,
  selectedId,
  isIndeterminate,
}) => {
  if (!options || _.isEmpty(options)) return null;

  return (
    <div className={classes.sideNavigationContainer}>
      <SideNavigation
        padding="8px 0px"
        theme="white"
        rootKey="root"
        dsVersion="2.0"
        onClick={onClick}
        options={options}
        haveCheckbox={true}
        showCheckboxesForAll={true}
        selectedOptionKey={selectedId}
        onCheckboxClick={e => onToggle(e, type)}
        testId={`assessment-learning-goals-navigation-${type}`}
        checkboxesState={_.map(_.get(options, "0.children", []), id => ({
          key: id,
          isChecked: isChecked(id, type),
          isIndeterminate: isIndeterminate(id, type),
        }))}
      />
    </div>
  );
};

// * Radio Navigation Component - Used for single year selection (MYP Objectives)
const RadioSideNavigation = ({ options, onClick, selectedId }) => {
  if (!options || _.isEmpty(options)) return null;

  return (
    <div className={classes.sideNavigationContainer}>
      <SideNavigation
        padding="8px 0px"
        theme="white"
        rootKey="root"
        dsVersion="2.0"
        onClick={onClick}
        options={options}
        selectedOptionKey={selectedId}
        testId="assessment-learning-goals-navigation-radio"
      />
    </div>
  );
};

// * Nested Checklist Rendering Component
export const CheckboxList = ({
  options,
  onToggle,
  isChecked,
  getLearningGoalsTagCountTooltip,
  hasNestedChildren,
}) => {
  return (
    <div className={classes.checkListContainer}>
      {_.map(options, item => {
        const { depth = 1 } = item;
        const checkListLabelClasses = classNames({
          "text-label-l": true,
          "text-label": depth > 2,
          [classes.checkListLabelDepth3]: _.isEqual(depth, 3),
          [classes.checkListLabelDepth4]: _.isEqual(depth, 4),
        });
        const checkListItemClasses = classNames({
          [classes.itemContainer]: true,
          [classes.checkListItemDepth3]: _.isEqual(depth, 3),
          [classes.checkListItemDepth4]: _.isEqual(depth, 4),
          [classes.checkListItemDepth5]: _.isEqual(depth, 5),
        });
        const checkboxItemLabelClasses = classNames({
          "text-label-l": _.isEqual(depth, 2) && hasNestedChildren,
          [classes.checkListItemLabel]: depth > 1,
        });

        return !_.isEmpty(item?.childNodes) ? (
          <div key={item.id} className={classes.checkListContainer}>
            <div className={checkListLabelClasses}>{item.label}</div>
            <CheckboxList
              onToggle={onToggle}
              isChecked={isChecked}
              options={item?.childNodes}
              getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
              hasNestedChildren={hasNestedChildren}
            />
          </div>
        ) : (
          <div className={checkListItemClasses} key={item.id}>
            <Checkbox
              key={item.id}
              dsVersion="2.0"
              isChecked={isChecked(item.id, "leaf")}
              onChange={() => onToggle(item.id, "leaf")}
              labelClassName={checkboxItemLabelClasses}
            >
              {item.label}
            </Checkbox>
            {_.isFunction(getLearningGoalsTagCountTooltip)
              ? getLearningGoalsTagCountTooltip(item.id)
              : null}
          </div>
        );
      })}
    </div>
  );
};

// * Assessment Other Learning Goals Component - Modal Body
const AssessmentOtherLearningGoalsModalBody = props => {
  const {
    selectedGoalsValues,
    setSelectedGoalsValues,
    learningGoalsData,
    allLearningGoals,
    selectedLearningGoal,
    setSelectedLearningGoal,
    getLearningGoalsTagCountTooltip,
  } = props;
  const { t } = useI18n();

  const [selectedSecondaryId, setSelectedSecondaryId] = useState(null);

  // * Check if current learning goal is MYP Objectives with single year selection
  const isSingleYearObjectives = useMemo(() => {
    const selectedType = _.get(
      _.find(allLearningGoals, { key: selectedLearningGoal }),
      "type"
    );
    return selectedType === ELEMENT_TYPE_MYP_OBJECTIVES;
  }, [allLearningGoals, selectedLearningGoal]);

  // * Memoized tree data and learning goal options
  const learningGoalsOptions = useMemo(() => {
    return getLearningGoalsWithFormattedOptionsForSideNav({
      options: learningGoalsData,
      allLearningGoals: allLearningGoals,
      suffix: <ChevronRightOutlined variant="neutral" type="plain" />,
    });
  }, [learningGoalsData, allLearningGoals]);

  // * Side navigation options for secondary level, Only when levels/depths > 1
  const secondarySideNavigationOptions = useMemo(() => {
    const selectedLearningGoalData = _.find(learningGoalsData, {
      id: selectedLearningGoal,
    });
    if (!selectedLearningGoal || !selectedLearningGoalData) return [];
    const hasOnlyOneLevel = selectedLearningGoalData?.hasLevels
      ? selectedLearningGoalData?.levels?.length <= 1
      : selectedLearningGoalData?.depths?.length <= 1;
    if (hasOnlyOneLevel) return []; // * Hide secondary side nav if only one level/depth
    return getLearningGoalsWithFormattedOptionsForSideNav({
      options: _.get(selectedLearningGoalData, "childNodes", []),
      suffix: <ChevronRightOutlined variant="neutral" type="plain" />,
    });
  }, [selectedLearningGoal, learningGoalsData]);

  // * Main content data based on selected learning goal and secondary side nav
  const {
    mainContentOptions = [],
    selectedLearningGoalData = {},
    selectedSecondaryNode = {},
    hasNestedChildren = false,
  } = useMemo(() => {
    const selectedLearningGoalData = _.find(learningGoalsData, {
      id: selectedLearningGoal,
    });
    const levels = _.get(selectedLearningGoalData, "levels", []);
    if (!selectedLearningGoalData) return {};

    const childNodes = _.get(selectedLearningGoalData, "childNodes", []);
    const secondaryChildNodes = _.find(childNodes, { id: selectedSecondaryId });
    if (secondaryChildNodes) {
      const mainContentOptions = _.get(secondaryChildNodes, "childNodes", []);
      const hasNestedChildren = _.some(
        mainContentOptions,
        node => !_.isEmpty(_.get(node, "childNodes", []))
      );
      return {
        mainContentOptions,
        selectedLearningGoalData,
        selectedSecondaryNode: secondaryChildNodes,
        hasNestedChildren,
      };
    } else if (levels.length <= 1) {
      return {
        mainContentOptions: childNodes,
        selectedLearningGoalData,
      };
    }
    return {};
  }, [learningGoalsData, selectedLearningGoal, selectedSecondaryId]);

  // * On mounting set first learning goal as selected
  useEffect(() => {
    const childNodes = _.get(learningGoalsOptions, "0.children", []);
    if (!selectedLearningGoal && !_.isEmpty(childNodes)) {
      setSelectedSecondaryId(null);
      setSelectedLearningGoal(_.first(childNodes));
    }
  }, [learningGoalsOptions, selectedLearningGoal]);

  // * On mounting set first secondary side nav option as selected
  useEffect(() => {
    const childNodes = _.get(secondarySideNavigationOptions, "0.children", []);
    if (!selectedSecondaryId && _.first(childNodes)) {
      setSelectedSecondaryId(_.first(childNodes));
    }
  }, [
    secondarySideNavigationOptions,
    selectedLearningGoal,
    selectedSecondaryId,
  ]);

  // * Handle secondary year click for single year objectives (radio behavior)
  const handleSecondaryYearClick = useCallback(
    _id => {
      if (isSingleYearObjectives && _id !== selectedSecondaryId) {
        // Keep only selections that belong to the newly selected year
        const newYearLeafIds = getAllLeafNodes(learningGoalsData, _id);
        const currentValues = _.get(
          selectedGoalsValues,
          selectedLearningGoal,
          []
        );
        const filteredValues = _.filter(currentValues, id =>
          _.includes(newYearLeafIds, id)
        );
        setSelectedGoalsValues({
          ...selectedGoalsValues,
          [selectedLearningGoal]: filteredValues,
        });
      }
      setSelectedSecondaryId(_id);
    },
    [
      isSingleYearObjectives,
      selectedSecondaryId,
      learningGoalsData,
      selectedLearningGoal,
      selectedGoalsValues,
      setSelectedGoalsValues,
    ]
  );

  // * On toggle of checklist item (leaf and non-leaf both)
  const onToggle = (id, type) => {
    const allValues = _.flatten(_.values(selectedGoalsValues));
    const childIds =
      type === "leaf" ? [id] : getAllLeafNodes(learningGoalsData, id);
    const isSelected = _.every(childIds, uid => _.includes(allValues, uid));
    if (isSelected) {
      const getValues = ids => _.filter(ids, id => !_.includes(childIds, id));
      setSelectedGoalsValues(_.mapValues(selectedGoalsValues, getValues));
    } else {
      const key = type === "primary" ? id : selectedLearningGoal;
      const selectedIds = _.get(selectedGoalsValues, key, []);
      const updatedIds = _.uniq([...selectedIds, ...childIds]);
      setSelectedGoalsValues({ ...selectedGoalsValues, [key]: updatedIds });
    }
  };

  // * To check whether item is checked (leaf and non-leaf both)
  const isChecked = (id, type) => {
    const _values = _.flatten(_.values(selectedGoalsValues));
    if (_.isEmpty(_values) || _.isEmpty(learningGoalsData)) return false;
    if (type === "leaf") return _.includes(_values, id);
    const allChildIds = getAllLeafNodes(learningGoalsData, id);
    return _.every(allChildIds, childId => _.includes(_values, childId));
  };

  // * To check whether item is indeterminate (only for non-leaf)
  const isIndeterminate = (id, type) => {
    const _values = _.flatten(_.values(selectedGoalsValues));
    if (_.isEmpty(_values) || _.isEmpty(learningGoalsData)) return false;
    if (type === "leaf" || isChecked(id, type)) return false;
    const allChildIds = getAllLeafNodes(learningGoalsData, id);
    return _.some(allChildIds, childId => _.includes(_values, childId));
  };

  // * Empty state label- selected secondary node label or selected learning goal label
  const emptyStateLabel =
    _.get(selectedSecondaryNode, "label", "") ||
    _.get(selectedLearningGoalData, "label", "");

  return (
    <div className={classes.modalBodyContent}>
      <CommonSideNavigation
        type="primary"
        onToggle={onToggle}
        isChecked={isChecked}
        options={learningGoalsOptions}
        selectedId={selectedLearningGoal}
        isIndeterminate={isIndeterminate}
        onClick={_id => {
          setSelectedSecondaryId(null);
          setSelectedLearningGoal(_id);
        }}
      />
      {isSingleYearObjectives ? (
        <RadioSideNavigation
          options={secondarySideNavigationOptions}
          selectedId={selectedSecondaryId}
          onClick={handleSecondaryYearClick}
        />
      ) : (
        <CommonSideNavigation
          type="secondary"
          onToggle={onToggle}
          isChecked={isChecked}
          selectedId={selectedSecondaryId}
          isIndeterminate={isIndeterminate}
          onClick={_id => setSelectedSecondaryId(_id)}
          options={secondarySideNavigationOptions}
        />
      )}
      <div className={classes.mainContentContainer}>
        {_.isEmpty(mainContentOptions) ? (
          <EmptyState
            dsVersion="2.0"
            illustration={NoServiceAsActionInsightsIllustration}
            title={t("curriculumMaps:maps_empty_state_text", {
              label: emptyStateLabel,
            })}
            subtitle={t("worksheet:learning_goals_empty_state_subtext", {
              label: emptyStateLabel,
            })}
          />
        ) : (
          <CheckboxList
            onToggle={onToggle}
            isChecked={isChecked}
            options={mainContentOptions}
            getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
            hasNestedChildren={hasNestedChildren}
          />
        )}
      </div>
    </div>
  );
};

export default AssessmentOtherLearningGoalsModalBody;
